/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo } from 'react';
import { useConfigStore } from '@/stores';
import type { FloorInfo } from '@/services/dto/kiosk-config';
import { FACILITY_SEARCH_MODE } from '@/enums/facility';
import poiUtils from '@/utils/poi';
import { getFacilityCategories, getFacilityMenu, resolveFacilityPoiContents } from '@/utils/facility';
import {
    isDutyFreePoi,
    getDutyFreeBuildingName,
    DUTY_FREE_BUILDING_ID,
    BRANCH_BUILDINGS,
    getBranchBuildingName,
    isBranchStorePoi,
    isBranchBuildingCode,
} from '@/utils/shop';
import type { TFunction } from 'i18next';
import { STORE_CODE_MAP_JAMSIL } from '@/constants/app';

const getFloorCode = (floor: FloorInfo): string => (floor as FloorInfo & { floor_code?: string }).floor_code ?? floor.floorCode;

const getFloorLabel = (floor: FloorInfo, language: string, fallbackLang = 'ko'): string => {
    const name = floor.name;
    if (Array.isArray(name)) {
        return name.find((item) => item.code === language)?.name ?? name.find((item) => item.code === fallbackLang)?.name ?? '';
    }
    return name?.[language] || name?.[fallbackLang] || '';
};

const parseFloorOrder = (label: string): number => {
    const basementMatch = label.match(/^B(\d+)$/i);
    if (basementMatch) return -Number(basementMatch[1]);

    const floorMatch = label.match(/^(\d+)F$/i);
    if (floorMatch) return Number(floorMatch[1]);

    return 0;
};

const sortFloorsByOrder = <T extends { order: number }>(floors: T[]): T[] => [...floors].sort((a, b) => a.order - b.order);

export const useHasMultipleFacilityBuildings = (): boolean => {
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const buildingMenus = useConfigStore((state) => state.config.server?.buildingMenus);
    const language = useConfigStore((state) => state.config.ui.language);
    const mainLanguage = useConfigStore((state) => state.config.ui.mainLanguage);
    const storeCode = useConfigStore((state) => state.config.server?.store?.storeCode);

    return useMemo(() => {
        if (buildings && buildings.length > 1) return true;
        if (!buildings || buildings.length === 0) return false;

        if (storeCode === STORE_CODE_MAP_JAMSIL) return true;

        const facilityMenu = getFacilityMenu(buildingMenus, language, mainLanguage);
        const pois = facilityMenu?.poiContents ?? [];

        if (pois.some((poi) => isDutyFreePoi(poi, language))) return true;

        return false;
    }, [buildings, buildingMenus, language, mainLanguage, storeCode]);
};

export const useFacilityBuildingOptions = (t: TFunction<'translation', undefined>) => {
    const language = useConfigStore((state) => state.config.ui.language);
    const mainLanguage = useConfigStore((state) => state.config.ui.mainLanguage);
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const buildingMenus = useConfigStore((state) => state.config.server?.buildingMenus);
    const storeCode = useConfigStore((state) => state.config.server?.store?.storeCode);

    return useMemo(() => {
        const allBuildingOption = {
            label: t('shop.search.text.content.all_building'),
            value: 'all',
        };

        const listBuildings =
            storeCode === STORE_CODE_MAP_JAMSIL
                ? []
                : buildings?.map((building) => ({
                      label: poiUtils.getBuildingLocalizedName(building, language, mainLanguage),
                      value: building.id,
                  })) || [];

        if (storeCode === STORE_CODE_MAP_JAMSIL) {
            Object.keys(BRANCH_BUILDINGS).forEach((code) => {
                listBuildings.push({
                    label: getBranchBuildingName(code, language),
                    value: code,
                });
            });
        } else {
            const facilityMenu = getFacilityMenu(buildingMenus, language, mainLanguage);
            const pois = facilityMenu?.poiContents ?? [];

            if (pois.some((poi) => isDutyFreePoi(poi, language))) {
                listBuildings.push({
                    label: getDutyFreeBuildingName(language),
                    value: DUTY_FREE_BUILDING_ID,
                });
            }
        }

        return listBuildings.length > 1 ? [allBuildingOption, ...listBuildings] : listBuildings;
    }, [language, mainLanguage, buildings, buildingMenus, storeCode, t]);
};

const buildMergedFacilityFloors = (
    buildings: any[],
    floorIds: Set<string>,
    language: string,
    allFloorOption: { id: string; label: string; value: string; data: null }
) => {
    const isMultiBuilding = buildings.length > 1;
    const labelToFloorInfo = new Map<string, { codes: Set<string>; order: number }>();

    buildings.forEach((building: any) => {
        building.floors?.forEach((floor: FloorInfo) => {
            const floorCode = getFloorCode(floor);
            if (!floorIds.has(floorCode)) return;

            const label = getFloorLabel(floor, language);
            if (!label) return;

            const existing = labelToFloorInfo.get(label);
            if (existing) {
                existing.codes.add(floorCode);
            } else {
                const order = isMultiBuilding ? parseFloorOrder(label) : (floor.order ?? 0);
                labelToFloorInfo.set(label, { codes: new Set([floorCode]), order });
            }
        });
    });

    if (labelToFloorInfo.size === 0) return [allFloorOption];

    const mergedFloors = Array.from(labelToFloorInfo.entries()).map(([label, info]) => ({
        id: label,
        label,
        value: label,
        data: Array.from(info.codes),
        order: info.order,
    }));

    return [allFloorOption, ...sortFloorsByOrder(mergedFloors)];
};

export const useFacilityFloorList = (selectedBuildingId: string | null, t: TFunction) => {
    const language = useConfigStore((state) => state.config.ui.language);
    const mainLanguage = useConfigStore((state) => state.config.ui.mainLanguage);
    const buildings = useConfigStore((state) => state.config.server?.store?.buildings);
    const buildingMenus = useConfigStore((state) => state.config.server?.buildingMenus);

    return useMemo(() => {
        const allFloorOption = {
            id: 'all',
            label: t('shop.search.text.content.all_floor'),
            value: 'all',
            data: null,
        };

        if (!buildings) {
            return [allFloorOption];
        }

        const facilityMenu = getFacilityMenu(buildingMenus, language, mainLanguage);
        const pois = resolveFacilityPoiContents(facilityMenu, selectedBuildingId, FACILITY_SEARCH_MODE.SEARCH_BY_FLOOR);

        if (selectedBuildingId === DUTY_FREE_BUILDING_ID) {
            const dutyFreeFloorIds = new Set(pois.filter((poi) => isDutyFreePoi(poi, language)).map((poi) => poi.studioFloorId));
            return buildMergedFacilityFloors(buildings, dutyFreeFloorIds, language, allFloorOption);
        }

        if (isBranchBuildingCode(selectedBuildingId)) {
            const branchFloorIds = new Set(
                pois.filter((poi) => isBranchStorePoi(poi, selectedBuildingId!, language)).map((poi) => poi.studioFloorId)
            );
            return buildMergedFacilityFloors(buildings, branchFloorIds, language, allFloorOption);
        }

        const poiFloorIds = new Set(pois.map((poi) => poi.studioFloorId));

        if (selectedBuildingId === 'all') {
            return buildMergedFacilityFloors(buildings, poiFloorIds, language, allFloorOption);
        }

        const buildingId = selectedBuildingId || buildings[0]?.id;
        if (!buildingId) return [allFloorOption];

        const building = buildings.find((b) => b.id === buildingId);
        if (!building) return [allFloorOption];

        const nonDutyFreeFloorIds = new Set(pois.filter((poi) => !isDutyFreePoi(poi, language)).map((poi) => poi.studioFloorId));

        const floors = sortFloorsByOrder(
            (building.floors ?? [])
                .map((floor) => ({
                    id: floor.id,
                    label: getFloorLabel(floor, language),
                    value: floor.id,
                    data: floor,
                    order: floor.order ?? 0,
                }))
                .filter((floor) => floor.label && nonDutyFreeFloorIds.has(getFloorCode(floor.data as FloorInfo)))
        );

        return floors.length > 0 ? [allFloorOption, ...floors] : [allFloorOption];
    }, [language, mainLanguage, buildings, selectedBuildingId, buildingMenus, t]);
};

export const useFacilityCategories = () => {
    const buildingMenus = useConfigStore((state) => state.config.server?.buildingMenus);
    const language = useConfigStore((state) => state.config.ui.language);
    const mainLanguage = useConfigStore((state) => state.config.ui.mainLanguage);

    return useMemo(() => getFacilityCategories(buildingMenus, language, mainLanguage), [buildingMenus, language, mainLanguage]);
};
